import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, LinearProgress } from "@material-ui/core";
import AddOutlinedIcon from "@material-ui/icons/AddOutlined";
import EventNoteOutlinedIcon from "@material-ui/icons/EventNoteOutlined";
import useStyles from "./styles";
import CrmService, { apiError, fmtDate } from "./crmService";
import { EmptyState, PageHead, useCrmSettings, useToast } from "./crmUi";
import EnquiryCard from "./EnquiryCard";
import FollowUpDialog from "./FollowUpDialog";
import useAddEnquiry from "./useAddEnquiry";

const BUCKETS = [
  { key: "overdue", label: "Overdue", color: "#b3261e", empty: "Nothing overdue. Well done." },
  { key: "today", label: "Due today", color: "#c27a00", empty: "No follow-ups due today." },
  { key: "upcoming", label: "Next 7 days", color: "#1f8a5b", empty: "Nothing planned for the next 7 days." },
  { key: "stale", label: "No follow-up", color: "#8a8a8a", empty: "Every open enquiry was followed up recently." },
];

/** Admissions CRM home: what to do today, with one-tap call / WhatsApp / log. */
export default function CrmToday() {
  const classes = useStyles();
  const history = useHistory();
  const { tz, error: settingsError } = useCrmSettings();
  const [summary, setSummary] = useState(null);
  const [bucket, setBucket] = useState(null);
  const [list, setList] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [logFor, setLogFor] = useState(null);
  const [toast, toastNode] = useToast();

  const load = useCallback(async (which) => {
    setLoading(true);
    setError("");
    try {
      const s = await CrmService.summary();
      setSummary(s);
      let b = which;
      if (!b) b = s.overdue ? "overdue" : s.today ? "today" : s.upcoming ? "upcoming" : "stale";
      setBucket(b);
      const res = await CrmService.list({ bucket: b, limit: 100 });
      setList(res);
    } catch (err) {
      setError(apiError(err, "Could not load follow-ups.").message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const refresh = (msg) => {
    if (msg) toast(msg);
    load(bucket);
  };
  const add = useAddEnquiry((e, msg) => refresh(msg));
  const current = BUCKETS.find((b) => b.key === bucket) || BUCKETS[0];
  const days = summary?.noFollowUpDays || 7;

  return (
    <div className={classes.page}>
      <PageHead
        title="Today's follow-ups"
        subtitle={`${fmtDate(new Date(), tz)}${summary ? ` · ${summary.open} open enquiries` : ""}`}
        actions={
          <>
            {/* <Button variant="outlined" onClick={add.openFull}>
              Add enquiry
            </Button>
            <Button variant="contained" color="primary" startIcon={<AddOutlinedIcon />} onClick={add.openQuick}>
              Quick add
            </Button> */}
          </>
        }
      />

      <div className={classes.tiles} role="tablist" aria-label="Follow-up lists">
        {BUCKETS.map((b) => (
          <button
            key={b.key}
            type="button"
            role="tab"
            aria-selected={bucket === b.key}
            className={`${classes.tile} ${bucket === b.key ? classes.tileActive : ""}`}
            onClick={() => load(b.key)}
          >
            <div className={classes.tileLabel}>
              <span className={classes.dot} style={{ background: b.color }} />
              {b.key === "stale" ? `No follow-up ${days}+ days` : b.label}
            </div>
            <div className={classes.tileValue} style={b.key === "overdue" && summary?.overdue ? { color: "#b3261e" } : null}>
              {summary ? summary[b.key] : "–"}
            </div>
          </button>
        ))}
      </div>

      <div className={classes.panelTight}>
        <div className={classes.toolbar}>
          <span className={classes.count}>
            {current.key === "stale" ? `No follow-up for ${days}+ days` : current.label}
            {!loading && ` · ${list.total}`}
          </span>
          <Button variant="text" style={{ color: "#0b5cc2" }} onClick={() => history.push("/app/crm/enquiries")}>
            All enquiries
          </Button>
        </div>
        {loading && <LinearProgress />}
        {(error || settingsError) && !loading ? (
          <EmptyState
            title="Could not load follow-ups"
            text={error || settingsError}
            action={
              <Button variant="contained" color="primary" onClick={() => load(bucket)}>
                Retry
              </Button>
            }
          />
        ) : !loading && list.items.length === 0 ? (
          <EmptyState
            icon={<EventNoteOutlinedIcon style={{ fontSize: 48 }} />}
            title={current.empty}
            text={summary && !summary.open ? "Add your first enquiry to start tracking admissions." : ""}
            action={
              summary && !summary.open ? (
                <Button variant="contained" color="primary" onClick={add.openQuick}>
                  Quick add
                </Button>
              ) : null
            }
          />
        ) : (
          <ul className={classes.cardList}>
            {list.items.map((e) => (
              <EnquiryCard
                key={e._id}
                enquiry={e}
                tz={tz}
                noFollowUpDays={days}
                onOpen={() => history.push(`/app/crm/enquiries/${e._id}`)}
                onLog={() => setLogFor(e)}
              />
            ))}
          </ul>
        )}
      </div>

      <FollowUpDialog
        open={Boolean(logFor)}
        enquiry={logFor}
        onClose={() => setLogFor(null)}
        onSaved={() => {
          setLogFor(null);
          refresh("Follow-up logged.");
        }}
      />
      {add.nodes}
      {toastNode}
    </div>
  );
}
